import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { ritentaSoloLeLetture } from '@/lib/ritentaSoloLeLetture';
import { guardiaOspite } from '@/services/guardiaOspite';
import { getQriotsAccessToken } from '@/lib/logto';
import type {
  ChampionMasterySummary,
  IdentityGame,
  LinkRiotAccountRequest,
  LinkedRiotAccountView,
  LiveGameInfo,
  MatchHistoryResult,
  MeAccountsResponse,
  MultiGameBadgesResult,
  MultiRegionResult,
  MultiSearchRequest,
  MultiSearchResponse,
  Platform,
  PlayerStatsAggregate,
  ProfileCard,
} from '@/types/qriots';

/**
 * QRiots API client. Talks to qriots-api.
 *
 * Auth is a Logto Bearer JWT with audience `https://qriots.qlash.gg/api`
 * in both dev and prod. The token is minted by getQriotsAccessToken() in
 * src/lib/logto.ts (Task 3.5) via refresh-token grant. The API resource
 * was registered on the Logto PROD tenant on 2026-05-22; see memory
 * reference_qriots_logto_prod_resource.
 *
 * Dev: env.qriotsApiUrl = '/qriots-api', Vite proxy forwards to MacMini.
 * Prod: env.qriotsApiUrl = 'https://qriots.qlash.gg' (direct).
 *
 * Tag: only `MyAccounts` (GET /v1/me/accounts, invalidated by link/unlink). The client is NOT read-only: it has three
 * mutations (`linkRiotAccount`, `unlinkRiotAccount`, and `multiSearch`, a batch READ sent as a POST).
 */
export const qriotsApi = createApi({
  reducerPath: 'qriotsApi',
  // ⛔ Prima era `retry(…, { maxRetries: 1 })`: ritenta OGNI errore (4xx e 429 compresi) e anche le MUTATION. Ora una
  // SCRITTURA non si ritenta MAI, una LETTURA una volta sola su rete/timeout/5xx (`lib/ritentaSoloLeLetture.ts`,
  // provata in `tests/ritentativi-altri-client.test.tsx`). `extraOptions: { maxRetries: 0 }` di un endpoint la
  // spegne del tutto (`liveGameLol`).
  baseQuery: guardiaOspite('qriots', ritentaSoloLeLetture(
    fetchBaseQuery({
      baseUrl: env.qriotsApiUrl,
      prepareHeaders: async (headers) => {
        const token = await getQriotsAccessToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        headers.set('x-platform-id', 'qlash');
        headers.set('Accept', 'application/json');
        return headers;
      },
    }),
  )),
  keepUnusedDataFor: 600,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  tagTypes: ['MyAccounts'],
  endpoints: (builder) => ({
    multiRegionLol: builder.query<
      MultiRegionResult,
      { gameName: string; tagLine: string }
    >({
      query: ({ gameName, tagLine }) => ({
        url: '/v1/multi-region/lol',
        params: { gameName, tagLine, platforms: 'default' },
      }),
      keepUnusedDataFor: 3600,
    }),

    multiGameBadges: builder.query<
      MultiGameBadgesResult,
      { gameName: string; tagLine: string; region: Platform }
    >({
      query: ({ gameName, tagLine, region }) => ({
        url: '/v1/multi-game/badges',
        params: { gameName, tagLine, region },
      }),
    }),

    lookupLol: builder.query<
      ProfileCard,
      { gameName: string; tagLine: string; region: Platform }
    >({
      query: ({ gameName, tagLine, region }) => ({
        url: '/v1/lookup/lol',
        params: { gameName, tagLine, region },
      }),
    }),

    lookupTft: builder.query<
      ProfileCard,
      { gameName: string; tagLine: string; region: Platform }
    >({
      query: ({ gameName, tagLine, region }) => ({
        url: '/v1/lookup/tft',
        params: { gameName, tagLine, region },
      }),
    }),

    lookupLor: builder.query<
      ProfileCard,
      { gameName: string; tagLine: string; region: Platform }
    >({
      query: ({ gameName, tagLine, region }) => ({
        url: '/v1/lookup/lor',
        params: { gameName, tagLine, region },
      }),
    }),

    lolMatchHistory: builder.query<
      MatchHistoryResult,
      { puuid: string; region: Platform; count?: number }
    >({
      query: ({ puuid, region, count = 10 }) => ({
        url: `/v1/match/lol/${encodeURIComponent(puuid)}/recent`,
        params: { region, count },
      }),
      // Match history pages display lists; keep cached longer than profile
      // (last match is already on the profile card; this is for the
      // "view all matches" deep-dive).
      keepUnusedDataFor: 900,
    }),

    lolStatsAggregate: builder.query<
      PlayerStatsAggregate,
      { puuid: string; region: Platform; window?: number }
    >({
      query: ({ puuid, region, window = 20 }) => ({
        url: `/v1/stats/lol/${encodeURIComponent(puuid)}/aggregate`,
        params: { region, window },
      }),
      keepUnusedDataFor: 900,
    }),

    lolMasteryFull: builder.query<
      ChampionMasterySummary,
      { puuid: string; region: Platform; count?: number }
    >({
      query: ({ puuid, region, count = 20 }) => ({
        url: `/v1/mastery/lol/${encodeURIComponent(puuid)}`,
        params: { region, count },
      }),
      keepUnusedDataFor: 1800,
    }),

    liveGameLol: builder.query<
      LiveGameInfo,
      { puuid: string; region: Platform }
    >({
      query: ({ puuid, region }) => ({
        url: `/v1/spectator/lol/${encodeURIComponent(puuid)}`,
        params: { region },
      }),
      // 404 = "not in game" — surfaces as an error in RTK Query; the
      // consuming component (RiotLolDetail) maps it to "no banner" via
      // classifyQriotsError(err).kind === 'not_found'. No retries; the
      // 60s poll provides natural retry.
      extraOptions: { maxRetries: 0 },
    }),

    // Identity — saved Riot accounts per Telegram user.
    // Requires a user-context JWT (refresh-grant from a real Logto session).
    myAccounts: builder.query<MeAccountsResponse, void>({
      query: () => ({ url: '/v1/me/accounts' }),
      providesTags: ['MyAccounts'],
      keepUnusedDataFor: 60,
    }),

    linkRiotAccount: builder.mutation<LinkedRiotAccountView, LinkRiotAccountRequest>({
      query: (body) => ({
        url: '/v1/me/accounts',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MyAccounts'],
    }),

    unlinkRiotAccount: builder.mutation<void, { puuid: string; game: IdentityGame }>({
      query: ({ puuid, game }) => ({
        url: `/v1/me/accounts/${encodeURIComponent(puuid)}`,
        method: 'DELETE',
        params: { game },
      }),
      invalidatesTags: ['MyAccounts'],
    }),

    // Multi-search / lobby scout (batch profile lookup, max 5 items).
    multiSearch: builder.mutation<MultiSearchResponse, MultiSearchRequest>({
      query: (body) => ({
        url: '/v1/multi-search',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useMultiRegionLolQuery,
  useMultiGameBadgesQuery,
  useLookupLolQuery,
  useLookupTftQuery,
  useLookupLorQuery,
  useLolMatchHistoryQuery,
  useLolStatsAggregateQuery,
  useLolMasteryFullQuery,
  useLiveGameLolQuery,
  useMyAccountsQuery,
  useLinkRiotAccountMutation,
  useUnlinkRiotAccountMutation,
  useMultiSearchMutation,
} = qriotsApi;
